import {
  DollarSign, Trash2, Download, Upload, Check, X, Square, CheckSquare, Ban, Edit2, FileX,
} from 'lucide-react';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const getPaymentStatusBadge = (status) => {
  if (status === 'pago') return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">PAGO</span>;
  return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">NÃO PAGO</span>;
};

const getInvoiceStatusBadge = (record) => {
  if ((record.invoice_status === 'nao_necessario' || record.no_invoice) && record.pdf_filename) {
    return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">Recibo Enviado</span>;
  }
  if (record.invoice_status === 'nao_necessario') return <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 rounded text-xs font-medium">Não Necessário</span>;
  if (record.no_invoice) return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-medium">Pagamento sem NF</span>;
  if (record.invoice_status === 'nf_enviada' || record.pdf_filename) return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">NF Enviada</span>;
  return <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">Esperando NF</span>;
};

/**
 * Payments records table — extracted from Budgets.js.
 * All handlers come from parent to keep state centralized.
 */
const PaymentsTable = ({
  records, userMap, isAdmin, user,
  selected, toggleSelect, toggleSelectAll,
  hasActiveFilters, onClearFilters,
  uploadingPdf, onPaymentStatusToggle, onToggleNoInvoice, onSetNotRequired,
  onEditClick, onPdfUpload, onPdfDownload, onDelete,
}) => {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700">
      <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              {isAdmin && (
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleSelectAll} data-testid="select-all-payments">
                    {selected.size === records.length && records.length > 0 ? <CheckSquare className="h-4 w-4 text-teal-400" /> : <Square className="h-4 w-4 text-slate-400" />}
                  </button>
                </th>
              )}
              {isAdmin && <th className="text-left text-slate-300 px-4 py-3 text-sm">Funcionário</th>}
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Projeto/Vídeo</th>
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Mês/Ano</th>
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Valor</th>
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Pagamento</th>
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Nota Fiscal</th>
              <th className="text-left text-slate-300 px-4 py-3 text-sm">Ações</th>
            </tr>
          </thead>
          <tbody>
            {records.map(record => (
              <tr key={record.id} className={`border-t border-slate-700 hover:bg-slate-700/30 ${selected.has(record.id) ? 'bg-teal-500/5' : ''}`}>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <button onClick={() => toggleSelect(record.id)} data-testid={`select-record-${record.id}`}>
                      {selected.has(record.id) ? <CheckSquare className="h-4 w-4 text-teal-400" /> : <Square className="h-4 w-4 text-slate-500" />}
                    </button>
                  </td>
                )}
                {isAdmin && (
                  <td className="px-4 py-3 text-sm relative group/emp">
                    <span className="text-white cursor-default">{record.employee_name}</span>
                    {userMap[record.employee_id] && (
                      <div className="absolute left-4 top-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 shadow-xl z-40 min-w-[220px] opacity-0 invisible group-hover/emp:opacity-100 group-hover/emp:visible transition-all pointer-events-none" data-testid={`tooltip-${record.id}`}>
                        <p className="text-white font-medium text-sm mb-1.5">{userMap[record.employee_id].artist_name || userMap[record.employee_id].name}</p>
                        <div className="space-y-1 text-xs">
                          <p className="text-slate-400">Chave PIX: <span className="text-slate-200">{userMap[record.employee_id].pix_key || '—'}</span></p>
                          <p className="text-slate-400">Banco: <span className="text-slate-200">{userMap[record.employee_id].pix_bank || '—'}</span></p>
                        </div>
                      </div>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-sm max-w-[200px]">
                  <div className="group relative">
                    <span className="text-white truncate block max-w-[200px]">{record.video_name?.length > 25 ? record.video_name.substring(0, 25) + '...' : record.video_name}</span>
                    {record.video_name?.length > 25 && (
                      <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 z-50 bg-slate-900 border border-slate-600 rounded-lg p-3 shadow-xl max-w-sm w-max">
                        {record.video_name.split(', ').map((proj, idx) => (
                          <div key={`${record.id}-proj-${idx}-${proj}`} className="text-sm text-white py-0.5">{proj}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400 text-sm">{MONTHS[record.month - 1]} / {record.year}</td>
                <td className="px-4 py-3 text-sm">
                  {record.adjusted_value && record.adjusted_value !== record.value ? (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 line-through">R$ {record.value.toFixed(2)}</span>
                      <span className="text-red-400 font-medium">R$ {record.adjusted_value.toFixed(2)}</span>
                    </div>
                  ) : (
                    <span className="text-green-400 font-medium">R$ {record.value.toFixed(2)}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {getPaymentStatusBadge(record.payment_status)}
                    {isAdmin && (
                      <button onClick={() => onPaymentStatusToggle(record.id, record.payment_status)}
                        className={`p-1 rounded transition-colors ${record.payment_status === 'pago' ? 'text-red-400 hover:bg-red-500/10' : 'text-green-400 hover:bg-green-500/10'}`}
                        title={record.payment_status === 'pago' ? 'Marcar como Não Pago' : 'Marcar como Pago'}>
                        {record.payment_status === 'pago' ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">{getInvoiceStatusBadge(record)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {isAdmin && (
                      <button onClick={() => onEditClick(record)}
                        className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded transition-colors" title="Editar"
                        data-testid={`edit-record-${record.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => onToggleNoInvoice(record.id, record.no_invoice)}
                        className={`p-1.5 rounded transition-colors ${record.no_invoice ? 'text-red-400 hover:bg-red-500/10' : 'text-slate-500 hover:bg-slate-500/10'}`}
                        title={record.no_invoice ? 'Reativar NF' : 'Marcar sem NF (-6%)'}
                        data-testid={`toggle-no-invoice-${record.id}`}
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => record.invoice_status === 'nao_necessario' ? onSetNotRequired(record.id, true) : onSetNotRequired(record.id)}
                        className={`p-1.5 rounded transition-colors ${record.invoice_status === 'nao_necessario' ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-500 hover:bg-slate-500/10'}`}
                        title={record.invoice_status === 'nao_necessario' ? 'Reativar NF' : 'Marcar NF como Não Necessário'}
                        data-testid={`not-required-${record.id}`}
                      >
                        <FileX className="h-4 w-4" />
                      </button>
                    )}
                    {record.invoice_status !== 'nao_necessario' && (isAdmin || record.employee_id === user?.id) && (
                      <label className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded cursor-pointer transition-colors" data-testid={`upload-pdf-${record.id}`}>
                        {uploadingPdf === record.id ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-400"></div> : <Upload className="h-4 w-4" />}
                        <input type="file" accept=".pdf" onChange={e => onPdfUpload(record.id, e.target.files[0])} className="hidden" disabled={uploadingPdf === record.id} />
                      </label>
                    )}
                    {record.pdf_filename && (
                      <button onClick={() => onPdfDownload(record.id, record.pdf_filename)}
                        className="p-1.5 text-green-400 hover:bg-green-500/10 rounded transition-colors" title={`Baixar: ${record.pdf_filename}`}
                        data-testid={`download-pdf-${record.id}`}>
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => onDelete(record.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded transition-colors" data-testid={`delete-record-${record.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {records.length === 0 && (
        <div className="text-center py-12">
          <DollarSign className="h-16 w-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">{hasActiveFilters ? 'Nenhum registro com os filtros selecionados.' : 'Nenhum registro encontrado para este período.'}</p>
          {hasActiveFilters && <button onClick={onClearFilters} className="text-teal-400 hover:text-teal-300 text-sm mt-2">Limpar filtros</button>}
        </div>
      )}
    </div>
  );
};

export default PaymentsTable;
